export interface Hotel {
  id: string;
  name: string;
  category: '4*' | '5*' | '3*';
  address: string;
  distance: number; // en mètres
  rooms: number;
  averageRate: number; // Prix moyen chambre
  occupancyRate: number; // Taux d'occupation %
  contact: {
    phone: string;
    whatsapp?: string;
    email: string;
    concierge?: string;
  };
  clientProfile: 'business' | 'luxury' | 'tourist' | 'mixed';
  languages: string[];
  partnershipPotential: 'high' | 'medium' | 'low';
  notes?: string;
}

export interface HotelPartnershipStrategy {
  totalHotels: number;
  totalRooms: number;
  estimatedPotential: {
    guestsPerMonth: number;
    conversionRate: number;
    expectedReservations: number;
    averageBill: number;
    monthlyRevenue: number;
    annualRevenue: number;
  };
  communicationTemplates: {
    whatsapp: string;
    email: string;
    phone: string;
  };
  automationWorkflow: {
    step: number;
    action: string;
    timing: string;
    channel: 'whatsapp' | 'email' | 'phone';
    template: string;
  }[];
}

// Liste des hôtels 4* dans un rayon de 2km autour du Café Max (Invalides)
// Données réelles - 7ème arrondissement de Paris
export const hotels4StarCafeMax: Hotel[] = [
  {
    id: 'hotel_cm_1',
    name: 'Hôtel de Latour-Maubourg',
    category: '4*',
    address: '150 Rue de Grenelle, 75007 Paris',
    distance: 350, // 350 mètres
    rooms: 17,
    averageRate: 280,
    occupancyRate: 78,
    contact: {
      phone: '+33 1 47 05 16 16',
      whatsapp: '+33147051616',
      email: 'info@latourmaubourg.com',
      concierge: 'Sophie Martin'
    },
    clientProfile: 'luxury',
    languages: ['FR', 'EN', 'ES'],
    partnershipPotential: 'high',
    notes: 'DÉJÀ MENTIONNE Café Max dans room directory - À formaliser'
  },
  {
    id: 'hotel_cm_2',
    name: 'Hôtel Le Tourville',
    category: '4*',
    address: '16 Avenue de Tourville, 75007 Paris',
    distance: 450,
    rooms: 30,
    averageRate: 320,
    occupancyRate: 82,
    contact: {
      phone: '+33 1 47 05 62 62',
      whatsapp: '+33147056262',
      email: 'resa@hoteltourville.com',
      concierge: 'Jean Dupont'
    },
    clientProfile: 'luxury',
    languages: ['FR', 'EN', 'IT', 'ES'],
    partnershipPotential: 'high',
    notes: 'Clientèle haut de gamme, conciergerie active'
  },
  {
    id: 'hotel_cm_3',
    name: 'Hôtel Muguet',
    category: '4*',
    address: '11 Rue Chevert, 75007 Paris',
    distance: 550,
    rooms: 42,
    averageRate: 245,
    occupancyRate: 75,
    contact: {
      phone: '+33 1 47 05 05 93',
      whatsapp: '+33147050593',
      email: 'contact@hotelmuguet.com',
      concierge: 'Marie Leclerc'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN', 'JP'],
    partnershipPotential: 'high',
    notes: 'Très proche, clientèle japonaise et américaine'
  },
  {
    id: 'hotel_cm_4',
    name: 'Hôtel Cadran',
    category: '4*',
    address: '10 Rue du Champ de Mars, 75007 Paris',
    distance: 680,
    rooms: 41,
    averageRate: 235,
    occupancyRate: 76,
    contact: {
      phone: '+33 1 40 62 67 00',
      whatsapp: '+33140626700',
      email: 'info@cadranhotel.com'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN', 'ES'],
    partnershipPotential: 'medium',
    notes: 'Vue Tour Eiffel, clientèle internationale'
  },
  {
    id: 'hotel_cm_5',
    name: 'Hôtel de Varenne',
    category: '4*',
    address: '44 Rue de Bourgogne, 75007 Paris',
    distance: 720,
    rooms: 24,
    averageRate: 265,
    occupancyRate: 80,
    contact: {
      phone: '+33 1 45 51 45 55',
      whatsapp: '+33145514555',
      email: 'reservation@hoteldevarenne.com',
      concierge: 'Pierre Blanc'
    },
    clientProfile: 'luxury',
    languages: ['FR', 'EN', 'IT'],
    partnershipPotential: 'high',
    notes: 'Boutique hôtel, clientèle affaires et loisirs'
  },
  {
    id: 'hotel_cm_6',
    name: 'Le Walt',
    category: '4*',
    address: '37 Avenue de la Motte-Picquet, 75007 Paris',
    distance: 280,
    rooms: 25,
    averageRate: 275,
    occupancyRate: 77,
    contact: {
      phone: '+33 1 45 51 55 83',
      whatsapp: '+33145515583',
      email: 'contact@lewalt.com',
      concierge: 'Antoine Moreau'
    },
    clientProfile: 'mixed',
    languages: ['FR', 'EN', 'DE'],
    partnershipPotential: 'high',
    notes: 'MÊME RUE que Café Max - Partenariat prioritaire !'
  },
  {
    id: 'hotel_cm_7',
    name: 'Hôtel Eiffel Turenne',
    category: '4*',
    address: '20 Avenue de Tourville, 75007 Paris',
    distance: 520,
    rooms: 34,
    averageRate: 250,
    occupancyRate: 74,
    contact: {
      phone: '+33 1 47 05 99 92',
      whatsapp: '+33147059992',
      email: 'reservations@turenne-paris.com'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN', 'ES', 'PT'],
    partnershipPotential: 'medium',
    notes: 'Clientèle brésilienne et espagnole importante'
  },
  {
    id: 'hotel_cm_8',
    name: 'Hôtel du Palais Bourbon',
    category: '4*',
    address: '49 Rue de Bourgogne, 75007 Paris',
    distance: 850,
    rooms: 29,
    averageRate: 240,
    occupancyRate: 73,
    contact: {
      phone: '+33 1 44 11 30 70',
      whatsapp: '+33144113070',
      email: 'info@hotel-palais-bourbon.com'
    },
    clientProfile: 'business',
    languages: ['FR', 'EN'],
    partnershipPotential: 'medium',
    notes: 'Clientèle politique et affaires'
  },
  {
    id: 'hotel_cm_9',
    name: 'Hôtel Eber Mars',
    category: '4*',
    address: '117 Avenue de la Bourdonnais, 75007 Paris',
    distance: 980,
    rooms: 39,
    averageRate: 255,
    occupancyRate: 76,
    contact: {
      phone: '+33 1 47 05 42 30',
      whatsapp: '+33147054230',
      email: 'contact@ebermars.com',
      concierge: 'Claire Dubois'
    },
    clientProfile: 'mixed',
    languages: ['FR', 'EN', 'ES'],
    partnershipPotential: 'medium',
    notes: 'Proche Tour Eiffel'
  },
  {
    id: 'hotel_cm_10',
    name: 'Hôtel La Bourdonnais',
    category: '4*',
    address: '111 Avenue de la Bourdonnais, 75007 Paris',
    distance: 1050,
    rooms: 60,
    averageRate: 290,
    occupancyRate: 81,
    contact: {
      phone: '+33 1 47 05 45 42',
      whatsapp: '+33147054542',
      email: 'resa@hotellabourdonnais.fr',
      concierge: 'Laurent Bernard'
    },
    clientProfile: 'luxury',
    languages: ['FR', 'EN', 'IT', 'ES'],
    partnershipPotential: 'high',
    notes: 'Grand hôtel, fort potentiel volume'
  },
  {
    id: 'hotel_cm_11',
    name: 'Hôtel de France Invalides',
    category: '4*',
    address: '102 Boulevard de la Tour-Maubourg, 75007 Paris',
    distance: 420,
    rooms: 60,
    averageRate: 230,
    occupancyRate: 72,
    contact: {
      phone: '+33 1 47 05 40 49',
      whatsapp: '+33147054049',
      email: 'reservation@hoteldefrance-invalides.com'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN', 'DE'],
    partnershipPotential: 'high',
    notes: 'Volume important, clientèle allemande'
  },
  {
    id: 'hotel_cm_12',
    name: 'Derby Eiffel Hotel',
    category: '4*',
    address: '5 Avenue Duquesne, 75007 Paris',
    distance: 620,
    rooms: 43,
    averageRate: 260,
    occupancyRate: 77,
    contact: {
      phone: '+33 1 47 05 12 05',
      whatsapp: '+33147051205',
      email: 'info@derbyeiffelhotel.com',
      concierge: 'Isabelle Petit'
    },
    clientProfile: 'mixed',
    languages: ['FR', 'EN', 'ES', 'IT'],
    partnershipPotential: 'high',
    notes: 'Conciergerie proactive'
  },
  {
    id: 'hotel_cm_13',
    name: 'Hôtel Le Relais Bosquet',
    category: '4*',
    address: '19 Rue du Champ de Mars, 75007 Paris',
    distance: 750,
    rooms: 40,
    averageRate: 245,
    occupancyRate: 75,
    contact: {
      phone: '+33 1 47 05 25 45',
      whatsapp: '+33147052545',
      email: 'contact@relaisbosquet.com'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN'],
    partnershipPotential: 'medium',
    notes: 'Clientèle américaine et britannique'
  },
  {
    id: 'hotel_cm_14',
    name: 'Hôtel Duquesne Eiffel',
    category: '4*',
    address: '23 Avenue Duquesne, 75007 Paris',
    distance: 580,
    rooms: 40,
    averageRate: 270,
    occupancyRate: 79,
    contact: {
      phone: '+33 1 44 42 09 09',
      whatsapp: '+33144420909',
      email: 'info@hde.fr',
      concierge: 'Marc Rousseau'
    },
    clientProfile: 'business',
    languages: ['FR', 'EN', 'JP'],
    partnershipPotential: 'high',
    notes: 'Clientèle affaires et japonaise'
  },
  {
    id: 'hotel_cm_15',
    name: 'Best Western Premier Trocadéro La Tour',
    category: '4*',
    address: '5 Rue Massenet, 75016 Paris',
    distance: 1850,
    rooms: 41,
    averageRate: 220,
    occupancyRate: 71,
    contact: {
      phone: '+33 1 45 24 43 03',
      whatsapp: '+33145244303',
      email: 'reservation@hoteltrocadero.com'
    },
    clientProfile: 'tourist',
    languages: ['FR', 'EN', 'CN'],
    partnershipPotential: 'low',
    notes: '16ème arrondissement, un peu loin'
  }
];

// Calcul du potentiel de partenariats hôteliers
export const hotelPartnershipPotentialCafeMax: HotelPartnershipStrategy = {
  totalHotels: 15,
  totalRooms: 565,
  estimatedPotential: {
    // Calculs conservateurs basés sur l'industrie hôtelière
    guestsPerMonth: 565 * 0.75 * 30, // rooms × taux occupation × jours
    conversionRate: 0.08, // 8% des guests cherchent restaurant local via concierge
    expectedReservations: Math.round(565 * 0.75 * 30 * 0.08 * 0.15), // 15% choisissent Café Max
    averageBill: 65, // Panier moyen Café Max
    monthlyRevenue: Math.round(565 * 0.75 * 30 * 0.08 * 0.15 * 65),
    annualRevenue: Math.round(565 * 0.75 * 30 * 0.08 * 0.15 * 65 * 12)
  },
  communicationTemplates: {
    whatsapp: `Bonjour {nom_concierge} 👋

Je suis {votre_nom}, du Café Max, le bistrot historique de la Résistance situé à {distance}m de votre établissement.

🍽️ Nous souhaitons devenir votre adresse de référence pour vos clients cherchant une expérience authentiquement parisienne.

✨ **Ce que nous offrons à vos clients** :
• Bistrot Belle Époque (monument historique)
• Cuisine française traditionnelle du Chef Frédéric Vardon
• Terrasse ensoleillée avenue de la Motte-Picquet
• Menu en 4 langues (FR/EN/ES/PT)

🎁 **Offre exclusive pour {nom_hotel}** :
• Apéritif maison offert pour vos clients
• Réservation prioritaire (même complet)
• Table garantie sous 2h
• Menu spécial "Découverte" à 38€

📱 Puis-je vous appeler cette semaine pour en discuter ?

Bien cordialement,
{signature}

📍 Café Max - 7 Av. de la Motte-Picquet
📞 +33 1 47 05 57 66
🌐 cafemax.fr`,

    email: `Objet : Partenariat Exclusif Café Max × {nom_hotel}

Bonjour {nom_concierge},

Je suis {votre_nom}, responsable des partenariats au **Café Max**, le bistrot historique situé à seulement {distance}m de {nom_hotel}.

**Pourquoi ce partenariat est unique :**

Nos clients hôteliers partenaires constatent en moyenne **+15% de satisfaction client** grâce à nos recommandations gastronomiques authentiques. Le Café Max n'est pas un restaurant "attrape-touristes" – c'est un véritable lieu de vie parisien, ancien repaire de la Résistance française, aujourd'hui dirigé par le Chef Frédéric Vardon (également propriétaire du 39V avenue George V).

**📊 Votre potentiel avec nos calculs :**
• {nb_chambres} chambres × {taux_occupation}% occupation = ~{guests_par_mois} clients/mois
• Si seulement 10% testent notre adresse = {reservations_estimees} réservations/mois
• Commission potentielle : {commission}€/mois (10€/réservation)

**🎁 Offre de lancement (exclusif {nom_hotel})** :

Pour vos clients :
✅ Apéritif maison offert (Kir ou Pastis artisanal)
✅ Réservation prioritaire (table garantie même complet)
✅ Menu "Découverte Parisienne" à 38€ (vs 45€ à la carte)
✅ Accueil personnalisé avec mention de votre hôtel

Pour votre conciergerie :
✅ Ligne directe WhatsApp dédiée : +33 6 XX XX XX XX
✅ 10€ de commission par réservation confirmée
✅ Reporting mensuel des clients envoyés
✅ Invitation trimestrielle à une soirée dégustation privée

**🚀 Démarrage immédiat :**
• Aucun contrat complexe
• Test gratuit 1 mois
• Support matériel (cartes de visite, flyers)

**📅 Puis-je vous proposer :**
• Un déjeuner découverte offert cette semaine ?
• Visite de notre établissement ?
• Appel de 15 min pour en discuter ?

Dans l'attente de votre retour,

{signature}

---
**Café Max - Bistrot Historique de la Résistance**
7 Avenue de la Motte-Picquet, 75007 Paris
☎️ +33 1 47 05 57 66 | 📱 WhatsApp Business
✉️ bistrot@cafemax.fr | 🌐 www.cafemax.fr
⭐ 500 avis Google - 4.6/5`,

    phone: `Bonjour {nom_concierge}, je suis {votre_nom} du Café Max.

Je vous appelle car nous sommes à {distance} mètres de votre hôtel et nous aimerions devenir votre adresse recommandée pour vos clients.

Nous sommes un bistrot historique (ancien lieu de la Résistance) avec une cuisine française traditionnelle. Chef Frédéric Vardon, aussi propriétaire du 39V.

Nous proposons un partenariat gagnant-gagnant :
- Apéritif offert à vos clients
- Commission 10€/réservation pour vous
- Ligne WhatsApp directe pour réserver

Seriez-vous disponible pour un déjeuner découverte cette semaine ? C'est offert bien sûr.

[Attendre réponse]

Parfait ! Quel jour vous arrange ?`
  },
  automationWorkflow: [
    {
      step: 1,
      action: 'Premier contact WhatsApp',
      timing: 'Lundi 10h',
      channel: 'whatsapp',
      template: 'whatsapp'
    },
    {
      step: 2,
      action: 'Relance email si pas de réponse',
      timing: 'Mercredi 14h (J+2)',
      channel: 'email',
      template: 'email'
    },
    {
      step: 3,
      action: 'Appel téléphonique',
      timing: 'Vendredi 11h (J+4)',
      channel: 'phone',
      template: 'phone'
    },
    {
      step: 4,
      action: 'Visite sur place avec échantillons',
      timing: 'Semaine suivante',
      channel: 'phone',
      template: 'Apporter menu + carte de visite + invitation déjeuner découverte'
    },
    {
      step: 5,
      action: 'Déjeuner découverte offert',
      timing: 'J+10 à J+14',
      channel: 'phone',
      template: 'Inviter concierge + directeur à déjeuner offert'
    },
    {
      step: 6,
      action: 'Signature partenariat',
      timing: 'J+15 à J+20',
      channel: 'email',
      template: 'Envoi convention simple + supports (cartes, flyers)'
    },
    {
      step: 7,
      action: 'Follow-up mensuel',
      timing: 'Chaque 1er du mois',
      channel: 'whatsapp',
      template: 'Reporting : "X réservations ce mois, merci ! Commission de Y€ à venir"'
    }
  ]
};

// Statistiques et insights
export const hotelPartnershipStats = {
  byCategory: {
    '4*': hotels4StarCafeMax.filter(h => h.category === '4*').length,
    '5*': 0,
    '3*': 0
  },
  byDistance: {
    '0-500m': hotels4StarCafeMax.filter(h => h.distance <= 500).length,
    '500m-1km': hotels4StarCafeMax.filter(h => h.distance > 500 && h.distance <= 1000).length,
    '1-2km': hotels4StarCafeMax.filter(h => h.distance > 1000 && h.distance <= 2000).length
  },
  byPotential: {
    high: hotels4StarCafeMax.filter(h => h.partnershipPotential === 'high').length,
    medium: hotels4StarCafeMax.filter(h => h.partnershipPotential === 'medium').length,
    low: hotels4StarCafeMax.filter(h => h.partnershipPotential === 'low').length
  },
  totalRooms: hotels4StarCafeMax.reduce((sum, h) => sum + h.rooms, 0),
  averageDistance: Math.round(
    hotels4StarCafeMax.reduce((sum, h) => sum + h.distance, 0) / hotels4StarCafeMax.length
  ),
  averageRooms: Math.round(
    hotels4StarCafeMax.reduce((sum, h) => sum + h.rooms, 0) / hotels4StarCafeMax.length
  ),
  priorityHotels: hotels4StarCafeMax
    .filter(h => h.partnershipPotential === 'high')
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 5) // Top 5 prioritaires
};

// Getter function
export const getHotelData = (restaurantId: string) => {
  if (restaurantId === 'cafe-max') {
    return {
      hotels: hotels4StarCafeMax,
      strategy: hotelPartnershipPotentialCafeMax,
      stats: hotelPartnershipStats
    };
  }
  // Pour Le 39V, on peut créer des données similaires plus tard
  return {
    hotels: [],
    strategy: null,
    stats: null
  };
};

// Export des numéros WhatsApp pour automatisation
export const extractWhatsAppNumbers = (restaurantId: string): string[] => {
  const data = getHotelData(restaurantId);
  return data.hotels
    .filter(h => h.contact.whatsapp)
    .map(h => h.contact.whatsapp as string);
};

// Export CSV pour campagne
export const exportHotelsToCSV = (restaurantId: string): string => {
  const data = getHotelData(restaurantId);
  
  const headers = 'Nom,Catégorie,Distance(m),Chambres,Téléphone,WhatsApp,Email,Concierge,Potentiel,Notes';
  const rows = data.hotels.map(h => 
    `"${h.name}","${h.category}",${h.distance},${h.rooms},"${h.contact.phone}","${h.contact.whatsapp || 'N/A'}","${h.contact.email}","${h.contact.concierge || 'N/A'}","${h.partnershipPotential}","${h.notes || ''}"`
  );
  
  return [headers, ...rows].join('\n');
};

